import { Empty } from 'antd';
import React, { lazy, ReactNode, Suspense, useCallback, useMemo } from 'react';
import {
  AttachmentViewer,
  Drawer,
  DrawerTabs,
  ErrorHandler,
  History,
  LoadingState,
} from '@common/components';
import { useQuery, useNavigate } from '@common/hooks';
import { buildDialogUrl } from '@common/services';
import {
  ComplianceItemDrawerQuery,
  ComplianceItemDrawerQueryType,
  DetailItemAttachmentType,
} from '@onboarding/graphql';
import { ComplianceItemDrawerFooter } from './_compliance-item-drawer-footer';

const TabFilesStates = lazy(() =>
  import('./_compliance-item-drawer-tab-files-states').then((mod) => ({
    default: mod.TabFilesStates,
  }))
);

export function ComplianceItemDrawer({
  open,
  onClose,
  id = '',
}: {
  open: boolean;
  onClose: () => void;
  id?: string;
}) {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<ComplianceItemDrawerQueryType>(
    ComplianceItemDrawerQuery,
    {
      skip: !id,
      variables: {
        id,
      },
    }
  );

  const complianceItem = data?.worksListItem;

  const openUpdateDrawer = useCallback(() => {
    if (!complianceItem?.id) {
      return;
    }

    const updateDrawerUrl = buildDialogUrl('compliance-item-update-drawer-v2', {
      props: { complianceItemId: complianceItem.id },
    });

    navigate(updateDrawerUrl);
  }, [complianceItem?.id, navigate]);

  const canUpdate = complianceItem?.worksList.canUpdate.value || false;
  const canApproveReject =
    complianceItem?.worksList.canApproveReject.value || false;
  const hasAnyAttachment =
    !!complianceItem?.detailItem?.detailItemAttachments?.length;
  const complianceItemHistory = complianceItem?.history;

  const sortedAttachments = useMemo(
    () =>
      sortAttachmentsDesc(complianceItem?.detailItem?.detailItemAttachments),
    [complianceItem?.detailItem?.detailItemAttachments]
  );

  const tabs = useMemo(() => {
    const DetailsComponent = (
      <AttachmentViewer
        source={sortedAttachments}
        onEdit={canUpdate ? openUpdateDrawer : undefined}
      />
    );

    const tabs = [
      {
        title: 'View',
        content: DetailsComponent,
      },
      {
        title: 'Files',
        content: (
          <SuspenseComplianceItemDrawer>
            <TabFilesStates complianceItemId={id} />
          </SuspenseComplianceItemDrawer>
        ),
      },
      {
        title: 'History',
        content: complianceItemHistory?.length ? (
          <History data={complianceItemHistory} />
        ) : (
          <Empty />
        ),
      },
    ];

    return tabs.flatMap((tab) => (tab ? [tab] : []));
  }, [
    sortedAttachments,
    canUpdate,
    openUpdateDrawer,
    id,
    complianceItemHistory,
  ]);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        messageType="contextual"
        log="ComplianceItemDrawer"
      />
    );
  }

  return (
    <Drawer
      open={open}
      size="lg"
      onClose={onClose}
      title={loading ? 'Loading...' : complianceItem?.name}
      className="compliance-item-drawer"
      loading={loading}
      footer={
        complianceItem && (
          <ComplianceItemDrawerFooter
            onFinished={onClose}
            canApproveReject={canApproveReject}
            canUpdate={canUpdate}
            complianceItemId={complianceItem.id}
            complianceItemState={complianceItem.state}
            complianceItemHasAnyFile={hasAnyAttachment}
          />
        )
      }
    >
      <DrawerTabs tabs={tabs} />
    </Drawer>
  );
}

function sortAttachmentsDesc(
  attachment: DetailItemAttachmentType[] | undefined
): DetailItemAttachmentType[] {
  if (!attachment) {
    return [];
  }

  return [...attachment].sort(
    (a, b) =>
      new Date(b.file.createdAt).getTime() -
      new Date(a.file.createdAt).getTime()
  );
}

function SuspenseComplianceItemDrawer({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={<LoadingState className="wk-compliance-item-drawer__loader" />}
    >
      {children}
    </Suspense>
  );
}
